/* eslint-disable react-hooks/exhaustive-deps */
"use client"

import { useEffect, useState } from "react"
import { Box, Paper } from "@mui/material"
import { HorizontalBarChart } from "../../components/horizontal-bar-chart"
import { TimeSeriesChart, type ChartType, type Period } from "./time-series-chart"
import { FiltersSection } from "../../components/filters-section"
import { BoardProcessMenu } from "../../components/BoardProcessMenu"
import { BOARD_TYPE_BALANCE_TO_COLLECT, BOARD_TYPE_BALANCE_TO_PAY } from "../../constants/boardProcessConsts"
import { useDispatch, useSelector } from "react-redux"
import { AppDispatch, RootState } from "../../store"
import { refreshToken } from "../../store/slices/auth"
import { requestSignalCompaniesToPayData, requestSignalDimensionToPayData, requestSignalOriginToPayData, requestSignalPeriodsDelayDetailsToPayData, requestSignalPeriodsDelayProviderDetailsToPayData, requestSignalPeriodsDelayProvidersToPayData, requestSignalPeriodsDelayToPayData, requestSignalPeriodsToPayData,  requestSignalProjectsToPayData, requestSignalStatusToPayData } from "../../services/signalApi"
import { dimensionsReset, originsReset, periodsDelayDetailsReset, periodsDelayProviderDetailsReset, periodsDelayProvidersReset, periodsDelayReset, periodsReset, projectsReset, statusReset } from "../../store/slices/provider"

export enum ViewType {
  'Pagar' = 0,
  'Cobrar' = 1
}

const CHART_WIDTH_LEFT = 35
const CHART_WIDTH_RIGHT = 65

export function DelayDashboard({view}: {view: ViewType}) {
  
  const dispatch = useDispatch<AppDispatch>();
  const authState = useSelector((state: RootState) => state.auth);
  const providerState = useSelector((state: RootState) => state.provider);

  const [selectedDelayIndex, setSelectedDelayIndex] = useState(0)
  const [selectedSupplierIndex, setSelectedSupplierIndex] = useState(0)
  const [chartType, setChartType] = useState<ChartType>("bar")
  const [period, setPeriod] = useState<Period>("1")
  const [filterValues, setFilterValues] = useState<Record<string, string>>({
      coin: 'PEN',
      company: 'all',
      mode: 'Proyecto',
      project: 'all',
      period: 'all',
      status: 'all',
      origin: 'all',
      quantity: '100',
      searchText: '',
  })
  const [filterPreviousValues, setFilterPreviousValues] = useState<Record<string, string>>(filterValues)

  const selectedDelay = providerState.periodsDelay[selectedDelayIndex]
  const selectedSupplier = providerState.periodsDelayProviders[selectedSupplierIndex]
  
    
  useEffect(() => {
    if (authState.token && authState.moduleId && authState.userId && !authState.connectionId) {
        dispatch(refreshToken(authState.token));
    }
  }, [authState.token, authState.moduleId, authState.userId]);
    
  useEffect(() => {
    if (!authState.connectionId) {
        return;
    }
    if (!providerState.companies.length) requestSignalCompaniesToPayData();
    getProjectsData(filterValues, filterPreviousValues)
    getPeriodsData(filterValues, filterPreviousValues);
    getStatusData(filterValues, filterPreviousValues);
    getOriginData(filterValues, filterPreviousValues);
    getPeriodsDelayData(filterValues, filterPreviousValues);
    setFilterPreviousValues(filterValues);
  }, [authState.connectionId, filterValues]);


  useEffect(() => {
    if (!selectedDelay) {
      return;
    }
    getPeriodsDelayDetailsData(filterValues, filterPreviousValues);
  }, [selectedDelay, period]);

  useEffect(() => {
    if (!selectedDelay) {
      return;
    }
    getPeriodsDelayProvidersData(filterValues, filterPreviousValues);
  }, [selectedDelay, filterValues?.searchText]);

  useEffect(() => {
    if (!selectedDelay || !selectedSupplier) {
      return;
    }
    getPeriodsDelayProviderDetailsData(filterValues, filterPreviousValues);
  }, [period, selectedSupplier]);

  const getProjectsData = (
    filterValues: Record<string, string>, 
    filterPreviousValues: Record<string, string>
  ) => {
    if (
        filterValues.company === filterPreviousValues.company
        && filterValues.mode === filterPreviousValues.mode
        && providerState.projects.length !== 0
    ) {
        return;            
    }
    if (filterValues.mode === 'Proyecto') {
        dispatch(projectsReset());
        requestSignalProjectsToPayData(filterValues.company, view);
    } else {
        dispatch(dimensionsReset());
        requestSignalDimensionToPayData(filterValues.company, filterValues.mode, view);
    }
  }

  const getPeriodsData = (
    filterValues: Record<string, string>, 
    filterPreviousValues: Record<string, string>
  ) => {
    if (
        filterValues.company === filterPreviousValues.company
        && filterValues.mode === filterPreviousValues.mode
        && filterValues.project === filterPreviousValues.project
        && providerState.periods.length !== 0
    ) {
        return;            
    }
    dispatch(periodsReset());
    requestSignalPeriodsToPayData(filterValues.company, filterValues.project, view);
  }

  const getOriginData = (
    filterValues: Record<string, string>, 
    filterPreviousValues: Record<string, string>
  ) => {
    if (
        filterValues.company === filterPreviousValues.company
        && filterValues.mode === filterPreviousValues.mode
        && filterValues.project === filterPreviousValues.project
        && filterValues.period === filterPreviousValues.period
        && filterValues.status === filterPreviousValues.status
        && providerState.origins.length !== 0
    ) {
        return;            
    }
    dispatch(originsReset());
    requestSignalOriginToPayData(
        filterValues.company, 
        filterValues.project, 
        filterValues.period, 
        filterValues.status,
        view,
    );
  }

  const getStatusData = (
    filterValues: Record<string, string>, 
    filterPreviousValues: Record<string, string>
  ) => {
    if (
        filterValues.company === filterPreviousValues.company
        && filterValues.mode === filterPreviousValues.mode
        && filterValues.project === filterPreviousValues.project
        && filterValues.period === filterPreviousValues.period
        && providerState.status.length !== 0
    ) {
        return;            
    }
    dispatch(statusReset());
    requestSignalStatusToPayData(
        filterValues.company, 
        filterValues.project, 
        filterValues.period,
        view,
    );
  }

  const handleFilterChange = (filterId: string, value: string) => {
      setFilterValues((prev) => ({
          ...prev,
          [filterId]: value,
      }))
  }
  
  const getPeriodsDelayData = (
      filterValues: Record<string, string>, 
      filterPreviousValues: Record<string, string>
  ) => {
    if (
      filterValues.company === filterPreviousValues.company
      && filterValues.mode === filterPreviousValues.mode
      && filterValues.project === filterPreviousValues.project
      && filterValues.period === filterPreviousValues.period
      && filterValues.status === filterPreviousValues.status
      && filterValues.origin === filterPreviousValues.origin
      // && filterValues.quantity === filterPreviousValues.quantity
      // && filterValues.searchText === filterPreviousValues.searchText
      && providerState.periodsDelay.length !== 0
    ) {
        return;            
    }
    setSelectedDelayIndex(0)
    dispatch(periodsDelayReset());
    dispatch(periodsDelayDetailsReset());
    dispatch(periodsDelayProvidersReset());
    dispatch(periodsDelayProviderDetailsReset());
    requestSignalPeriodsDelayToPayData(
      filterValues.company, 
      filterValues.project, 
      filterValues.period, 
      filterValues.status, 
      filterValues.origin,
      filterValues.mode,
      view,
      filterValues.quantity,
      filterValues.searchText,
    );
  }

  const getPeriodsDelayDetailsData = (
      filterValues: Record<string, string>, 
      filterPreviousValues: Record<string, string>
  ) => {
      if (!selectedDelay?.CodRetraso) {
          return;
      }
      dispatch(periodsDelayDetailsReset());
      requestSignalPeriodsDelayDetailsToPayData(
        filterValues.company, 
        filterValues.project, 
        filterValues.period, 
        filterValues.status, 
        filterValues.origin,
        filterValues.mode,
        selectedDelay.CodRetraso,
        period,
        view,
        filterValues.quantity,
        filterValues.searchText,
      );
  }

  const getPeriodsDelayProvidersData = (
      filterValues: Record<string, string>, 
      filterPreviousValues: Record<string, string>
  ) => {
      if (!selectedDelay?.CodRetraso) {
          return;
      }
      dispatch(periodsDelayProvidersReset());
      dispatch(periodsDelayProviderDetailsReset());
      requestSignalPeriodsDelayProvidersToPayData(
        filterValues.company, 
        filterValues.project, 
        filterValues.period, 
        filterValues.status, 
        filterValues.origin,
        filterValues.mode,
        selectedDelay.CodRetraso,
        view,
        filterValues.quantity,
        filterValues.searchText,
      );
  }

  const getPeriodsDelayProviderDetailsData = (
      filterValues: Record<string, string>, 
      filterPreviousValues: Record<string, string>
  ) => {
      console.log('selectedDelay', selectedDelay)
      console.log('selectedSupplier', selectedSupplier)
      if (!selectedDelay || !selectedSupplier) {
          return;
      }
      dispatch(periodsDelayProviderDetailsReset());
      requestSignalPeriodsDelayProviderDetailsToPayData(
        filterValues.company, 
        filterValues.project, 
        filterValues.period, 
        filterValues.status, 
        filterValues.origin,
        filterValues.mode,
        selectedDelay.CodRetraso,
        selectedSupplier.CodProveedor,
        period,
        view,
        filterValues.quantity,
        filterValues.searchText,
      );
  }

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      
      <FiltersSection
        filters={[
            {
                label: "Moneda",
                value: "coin",
                options: [
                    { label: "Soles", value: "PEN" },
                    { label: "Dolares", value: "USD" },
                ],
            },
            {
                label: "Empresa",
                value: "company",
                options: [
                  { label: "Todos", value: "all" },
                  ...providerState.companies.map(c => ({
                      label: c.Descripcion,
                      value: c.CodEmpresa,
                  })),
                ],
            }, {
              label: "Modo",
              value: "mode",
              options: [
                  { label: "Proyecto", value: "Proyecto" },
                  { label: "Dimensión 1", value: "Dimension 1" },
                  { label: "Dimensión 2", value: "Dimension 2" },
                  { label: "Dimensión 3", value: "Dimension 3" },
                  { label: "Dimensión 4", value: "Dimension 4" },
                  { label: "Dimensión 5", value: "Dimension 5" },
              ],
            }, {
              label:  filterValues.mode,
              value: "project",
              type: 'search',
              options: [
                  { label: "Todos", value: "all" },
                  ...(filterValues.mode === 'Proyecto' 
                      ? providerState.projects.map(c => ({
                          label: c.Descripcion,
                          value: c.CodProyecto,
                      }))
                      : providerState.dimensions.map(c => ({
                          label: c.Descripcion,
                          value: c.CodProyectoVector,
                      }))
                  ),
              ],
            },
            {
              label: "Periodo",
              value: "period",
              options: [
                  { label: "Todos", value: "all" },
                  ...providerState.periods.map(c => ({
                      label: c.Descripcion,
                      value: c.CodPeriodoContable,
                  })),
              ],
            },
            {
              label: "Estado",
              value: "status",
              options: [
                  { label: "Todos", value: "all" },
                  ...providerState.status.map(c => ({
                      label: c.Estado,
                      value: c.Estado,
                  })),
              ],
            },
            {
              label: "Origen",
              value: "origin",
              options: [
                  { label: "Todos", value: "all" },
                  ...providerState.origins.map(c => ({
                      label: c.Origen ?? 'null',
                      value: c.Origen,
                  })),
              ],
            },
            // {
            //   label: "Primeros",
            //   value: "quantity",
            //   options: [
            //       { label: "200", value: "200" },
            //       { label: "100", value: "100" },
            //       { label: "50", value: "50" },
            //       { label: "10", value: "10" },
            //   ],
            // },
            // {
            //   label: "Búscar proveedor",
            //   value: "searchText",
            //   type: "text",
            //   debounce: 500,
            //   options: [],
            // },
        ]} 
        onFilterChange={handleFilterChange} 
        values={filterValues} 
        actions={
          <BoardProcessMenu
            boardType={view === ViewType.Pagar ? BOARD_TYPE_BALANCE_TO_PAY : BOARD_TYPE_BALANCE_TO_COLLECT}
          />
        }
    />
      <Box sx={{ height: "50%", display: "flex", gap: 2, p: 2, pb: 1, minHeight: '45vh', maxHeight: '45vh' }}>
        <Box sx={{ width: `${CHART_WIDTH_LEFT}%` }}>
          <Paper elevation={2} sx={{ height: "100%", p: 2, display: "flex", flexDirection: "column" }}>
            <HorizontalBarChart
              title="Periodo de retrasos"
              data={providerState.periodsDelay.map(p => (
                { 
                  id: p.CodRetraso,
                  label: p.Descripcion, 
                  value: (p as any)[`Costo${filterValues.coin}`], 
                  color: "rgba(147, 51, 234, 0.8)" 
                }
              ))}
              selectedIndex={selectedDelayIndex}
              onSelect={(index) => {
                setSelectedDelayIndex(index)
                setSelectedSupplierIndex(0)
              }}
              enablePagination={true}
              itemsPerPage={3}
            />
          </Paper>
        </Box>

        <Box sx={{ width: `${CHART_WIDTH_RIGHT}%` }}>
          <Paper elevation={2} sx={{ height: "100%", p: 2, display: "flex", flexDirection: "column" }}>
            <TimeSeriesChart
              title={`Saldos por periodo - ${selectedDelay?.Descripcion || ''}`}
              chartType={chartType}
              period={period}
              onChartTypeChange={setChartType}
              onPeriodChange={setPeriod}
              data={providerState.periodsDelayDetails.map(p => (
                { 
                  ...p,
                  label: p.id, 
                  value: (p as any)[`Costo${filterValues.coin}`], 
                  color: "rgba(147, 51, 234, 0.8)" 
                }
              ))}
              sCurveData={providerState.periodsDelayDetails.map(p => (
                { 
                  ...p,
                  label: p.id, 
                  value: (p as any)[`Costo${filterValues.coin}_Acumulado`],
                }
              ))}
              color="rgba(239, 68, 68, 0.8)"
              selectHeight={24}
              itemsPerPage={20}
              enablePagination={true}
            />
          </Paper>
        </Box>
      </Box>

      <Box sx={{ height: "50%", display: "flex", gap: 2, p: 2, pt: 1, minHeight: '45vh', maxHeight: '45vh' }}>
        <Box sx={{ width: `${CHART_WIDTH_LEFT}%` }}>
          <Paper elevation={2} sx={{ height: "100%", p: 2, display: "flex", flexDirection: "column" }}>
            <HorizontalBarChart
              title={`${view ? 'Clientes' : 'Proveedores'} con retraso - ${selectedDelay?.Descripcion || ''}`}
              data={providerState.periodsDelayProviders.map(p => (
                { 
                  id: p.CodProveedor,
                  label: p.Descripcion, 
                  value: (p as any)[`Costo${filterValues.coin}`], 
                  color: "rgba(147, 51, 234, 0.8)" 
                }
              ))}
              selectedIndex={selectedSupplierIndex}
              onSelect={setSelectedSupplierIndex}
              enablePagination={true}
              itemsPerPage={8}
            />
          </Paper>
        </Box>

        <Box sx={{ width: `${CHART_WIDTH_RIGHT}%` }}>
          <Paper elevation={2} sx={{ height: "100%", p: 2, display: "flex", flexDirection: "column" }}>
            <TimeSeriesChart
              title={`Saldos de ${view ? 'Cliente' : 'Proveedor'} por periodo - ${selectedSupplier?.Descripcion?.toUpperCase() || ''}`}
              chartType={chartType}
              period={period}
              onChartTypeChange={setChartType}
              onPeriodChange={setPeriod}
              data={providerState.periodsDelayProviderDetails.map(p => (
                { 
                  ...p,
                  label: p.id, 
                  value: (p as any)[`Costo${filterValues.coin}`], 
                  color: "rgba(147, 51, 234, 0.8)" 
                }
              ))}
              sCurveData={providerState.periodsDelayProviderDetails.map(p => (
                { 
                  ...p,
                  label: p.id, 
                  value: (p as any)[`Costo${filterValues.coin}_Acumulado`], 
                  color: "rgba(147, 51, 234, 0.8)" 
                }
              ))}
              color="rgba(20, 184, 166, 0.8)"
              selectHeight={24}
              itemsPerPage={20}
              enablePagination={true}
            />
          </Paper>
        </Box>
      </Box>
    </Box>
  )
}
